import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, SearchSelect } from "../../components/ui";
import "./staff.css";
import { staffApi, staffKeys } from "./api";
import { ACTIVITY_FAMILIES, ActivityFeed, CustomerSelect, useDebounced } from "./customers";

const rangeOptions = [
  { value: "24", label: "Last 24 hours" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
  { value: "2160", label: "Last 90 days" },
  { value: "0", label: "All time" },
];

/**
 * Everything the platform team has done, across every customer and the
 * deployment itself: who, what, to whom, from where. This is the answer to
 * "who gave them those credits" and "who changed the AI provider".
 */
export function StaffActivityPage() {
  const [qInput, setQInput] = useState("");
  const [family, setFamily] = useState("");
  const [actor, setActor] = useState("");
  const [orgId, setOrgId] = useState("");
  const [sinceHours, setSinceHours] = useState(720);
  const q = useDebounced(qInput.trim(), 250);
  const actors = useQuery({ queryKey: staffKeys.activityActors, queryFn: () => staffApi.activityActors(), staleTime: 60_000 });
  const filters = useMemo(
    () => ({ q: q || undefined, action: family || undefined, actor: actor || undefined, orgId: orgId || undefined, sinceHours }),
    [q, family, actor, orgId, sinceHours],
  );
  const filtered = !!(q || family || actor || orgId);

  return (
    <div className="activity-page">
      <PageHeader title="Activity" subtitle="Every action by the platform team, newest first.">
        <div className="toolbar customers-filters" aria-label="Activity filters">
          <input
            className="input customers-search"
            type="search"
            placeholder="Search customers, people, or details"
            aria-label="Search activity"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <CustomerSelect value={orgId} onChange={setOrgId} />
          <SearchSelect value={family} options={ACTIVITY_FAMILIES} onChange={setFamily} aria-label="Kind of action" searchPlaceholder="Search kinds…" />
          <SearchSelect
            value={actor}
            options={[{ value: "", label: "Anyone on the team" }, ...(actors.data?.items ?? []).map((e) => ({ value: e, label: e }))]}
            onChange={setActor}
            aria-label="Who"
            searchPlaceholder="Search people…"
          />
          <SearchSelect value={String(sinceHours)} options={rangeOptions} onChange={(v) => setSinceHours(Number(v))} aria-label="Time range" searchPlaceholder="Search ranges…" />
        </div>
      </PageHeader>
      <div className="card">
        <ActivityFeed
          filters={filters}
          emptyTitle={filtered ? "Nothing matches" : "No activity yet"}
          emptyHint={
            filtered ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setQInput("");
                  setFamily("");
                  setActor("");
                  setOrgId("");
                }}
              >
                Clear filters
              </button>
            ) : (
              "Actions by the platform team appear here as they happen."
            )
          }
        />
      </div>
    </div>
  );
}
